// authentication context for the "user" realm (normal flow).
// It reads/writes the user token from dedicated storage keys and exposes
//      a simple API for login/logout without coupling to any specific UI.

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { STORAGE_USER } from './storageKeys';
import { isExpiredFromExp } from './jwt';

const UserAuthContext = createContext(null);

//==============================================================================
// Hook to access user realm auth state.
//==============================================================================
export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error('useUserAuth must be used within <UserAuthProvider />');
  return ctx;
}

//==============================================================================1
// UserAuthProvider component to wrap your app (user realm).
//==============================================================================
export default function UserAuthProvider({ children }) {
  // Initialize from localStorage so refresh keeps the session.
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem(STORAGE_USER.token); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  // =============================================================================
  // Hydration + expiration check + multi-tab sync.
  // =============================================================================
  useEffect(() => {
    // 1) validate expiration (using `exp`)
    if (token && isExpiredFromExp(token)) {
      try { localStorage.removeItem(STORAGE_USER.token); } catch { }
      setToken(null);
    }
    setLoading(false);

    // 2) react to changes done in other tabs/windows
    const onStorage = (e) => {
      if (e.key === STORAGE_USER.token) {
        setToken(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);

  // =============================================================================
  // Programmatic login to be called by your login form later. 
  // =============================================================================
  const login = (jwtToken /* string */) => {
    // NOTE: no API calls here; just store the already-received token.
    try { localStorage.setItem(STORAGE_USER.token, jwtToken); } catch { }
    setToken(jwtToken);
  };

  // =============================================================================
  // Clear session for the user realm only.
  //
  // Options:
  //   - clearStorage (default: true)
  //       When true, clears all user-scoped persisted data (menus/permissions/profile
  //       and legacy user keys) in addition to the auth token.
  //       When false, only clears the auth token and in-memory state.
  // =============================================================================
  const logout = (options = { clearStorage: true }) => {
    const clearStorage = options?.clearStorage !== false;

    try {
      // Always clear the user auth token
      localStorage.removeItem(STORAGE_USER.token);

      // Optionally clear all other user realm persisted data
      if (clearStorage) {
        localStorage.removeItem(STORAGE_USER.menus);
        localStorage.removeItem(STORAGE_USER.permissions);
        localStorage.removeItem(STORAGE_USER.profile);

        // Legacy user keys (kept for backward compatibility while still referenced)
        localStorage.removeItem("token");
        localStorage.removeItem("Menus");
        localStorage.removeItem("permissions");
        localStorage.removeItem("Profile");
        localStorage.removeItem("PermissionGroup");
        localStorage.removeItem("acc");
        localStorage.removeItem("defaultAccount");
        localStorage.removeItem("name");
        localStorage.removeItem("uuid");
      }


    } catch { }

    // Always clear in-memory state
    setToken(null);
  };


  const value = useMemo(() => ({
    token,
    isAuthenticated: !!token,
    loading,
    login,
    logout,
  }), [token, loading]);

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
}
